export const PERCENT_OF_BASE_MODE = "PERCENT_OF_BASE";

export type OutcomeRegistryRow = {
  key: string;
  group: string;
  groupLabel?: string;
  groupTitle?: string;
  groupName?: string;
  label?: string;
  baseLocked?: boolean;
  baseIncluded?: boolean;
  baseSelectable?: boolean;
  componentsSelectable?: boolean;
  optionsFrom?: string;
  selectionMode?: string;
  selectionModeLabel?: string;
  selectionModeText?: string;
  selectionModeName?: string;
};

export type OutcomeRegistry = {
  coreOutcomes?: Record<string, Partial<OutcomeRegistryRow>>;
  customOutcomes?: OutcomeRegistryRow[];
  groupLabels?: Record<string, string>;
};

export type OutcomePolicyFormValue = {
  baseIncluded: boolean;
  baseLocked: boolean;
  selectedComponents: string[];
};

export type PayoutComponent = {
  componentType?: string;
  mode?: string;
  value?: number | string | null;
};

type TranslateFn = (key: string) => unknown;

const getText = (value: unknown): string => {
  const text = String(value ?? "").trim();
  return text;
};

const getTranslatedText = (
  t: TranslateFn | undefined,
  key: string,
  fallback: string,
): string => {
  if (!t || !key) return fallback;

  const translated = t(key);
  return translated && translated !== key ? String(translated) : fallback;
};

export const getCurrencySymbol = (code: string): string => {
  if (!code) return "";
  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code.toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency");
    return symbol ? symbol.value : code;
  } catch {
    if (code.toUpperCase() === "EUR") return "€";
    if (code.toUpperCase() === "USD") return "$";
    if (code.toUpperCase() === "GBP") return "£";
    return code;
  }
};

const getRegistryGroupLabel = (
  outcomeRegistry: unknown,
  groupKey: string,
  rowValue?: unknown,
): string => {
  const registry = outcomeRegistry as
    | (OutcomeRegistry & Record<string, unknown>)
    | undefined;
  const row = rowValue as Partial<OutcomeRegistryRow> | undefined;
  const groupLabels = registry?.groupLabels;
  const label =
    row?.groupLabel ??
    row?.groupTitle ??
    row?.groupName ??
    row?.group ??
    groupLabels?.[groupKey] ??
    registry?.[`${groupKey}Label`] ??
    registry?.[`${groupKey}Title`] ??
    "";

  return label ? String(label) : "";
};

export const formatRegistryText = (value: unknown): string => {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getOutcomeRegistryRows = (
  outcomeRegistry: unknown,
): OutcomeRegistryRow[] => {
  const rows: OutcomeRegistryRow[] = [];
  const registry = outcomeRegistry as
    | (OutcomeRegistry & Record<string, unknown>)
    | undefined;
  const coreOutcomes = registry?.coreOutcomes;
  const customOutcomes = registry?.customOutcomes;

  if (coreOutcomes && typeof coreOutcomes === "object") {
    Object.entries(coreOutcomes).forEach(([key, value]) => {
      const rowValue = value || {};
      rows.push({
        key,
        ...rowValue,
        group: getRegistryGroupLabel(
          outcomeRegistry,
          "coreOutcomes",
          rowValue,
        ),
      });
    });
  }

  if (Array.isArray(customOutcomes)) {
    customOutcomes.forEach((value) => {
      if (!value?.key) return;
      rows.push({
        ...value,
        key: value.key,
        group: getRegistryGroupLabel(
          outcomeRegistry,
          "customOutcomes",
          value,
        ),
      });
    });
  }

  return rows;
};

const getOutcomeGroupKey = (
  outcomeRegistry: unknown,
  row: OutcomeRegistryRow,
): "coreOutcomes" | "customOutcomes" => {
  const coreOutcomes = (outcomeRegistry as OutcomeRegistry | undefined)
    ?.coreOutcomes;
  const isCoreOutcome =
    !!row.key &&
    !!coreOutcomes &&
    typeof coreOutcomes === "object" &&
    Object.prototype.hasOwnProperty.call(coreOutcomes, row.key);

  return isCoreOutcome ? "coreOutcomes" : "customOutcomes";
};

const isDefaultGroupText = (text: string): boolean => {
  const normalized = text.toLowerCase().replace(/[\s_]+/g, "");
  return normalized === "coreoutcomes" || normalized === "customoutcomes";
};

export const getOutcomeGroupLabel = (
  outcomeRegistry: unknown,
  row: OutcomeRegistryRow,
  t?: TranslateFn,
): string => {
  const groupKey = getOutcomeGroupKey(outcomeRegistry, row);
  const fallback =
    groupKey === "coreOutcomes" ? "Core outcomes" : "Custom outcomes";
  const backendLabel =
    getText(row.group) ||
    getText(row.groupLabel) ||
    getText(row.groupTitle) ||
    getText(row.groupName);

  if (backendLabel && !isDefaultGroupText(backendLabel)) {
    return getTranslatedText(t, backendLabel, backendLabel);
  }

  return getTranslatedText(t, `payoutPolicy.${groupKey}`, fallback);
};

export const getOutcomeRegistryRowsForUi = (
  outcomeRegistry: unknown,
  t?: TranslateFn,
): OutcomeRegistryRow[] => {
  return getOutcomeRegistryRows(outcomeRegistry).map((row) => ({
    ...row,
    group: getOutcomeGroupLabel(outcomeRegistry, row, t),
  }));
};

export const getOutcomePolicyFormValue = (
  row: OutcomeRegistryRow,
  savedValue: unknown,
): OutcomePolicyFormValue => {
  const savedObject =
    savedValue && typeof savedValue === "object" && !Array.isArray(savedValue)
      ? (savedValue as Record<string, unknown>)
      : {};

  const baseIncluded =
    typeof savedObject.baseIncluded === "boolean"
      ? savedObject.baseIncluded
      : !!row.baseIncluded;

  const baseLocked =
    typeof row.baseLocked === "boolean"
      ? row.baseLocked
      : !!savedObject.baseLocked;

  const selectedComponents = Array.isArray(savedObject.selectedComponents)
    ? savedObject.selectedComponents
        .filter((value) => value !== undefined && value !== null)
        .map((value) => String(value))
    : [];

  return {
    ...savedObject,
    baseIncluded,
    baseLocked,
    selectedComponents,
  };
};

const getSavedOutcomeValue = (savedStepValue: unknown, outcomeKey: string) => {
  const stepValue = savedStepValue as Record<string, unknown> | undefined;
  const outcomes = stepValue?.outcomes as
    | Record<string, unknown>
    | undefined;
  return outcomes?.[outcomeKey] ?? stepValue?.[outcomeKey];
};

const getByPath = (source: unknown, path: string): unknown => {
  if (!source || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) {
    return (source as Record<string, unknown>)[path];
  }
  return path
    .split(".")
    .reduce(
      (accumulator: unknown, key) =>
        accumulator != null
          ? (accumulator as Record<string, unknown>)[key]
          : undefined,
      source,
    );
};

const getSelectableComponentKeys = (
  row: OutcomeRegistryRow,
  formData: unknown,
): Set<string> | null => {
  const optionsFrom = String(row.optionsFrom || "").trim();
  if (!optionsFrom) return null;

  const dotIndex = optionsFrom.indexOf(".");
  if (dotIndex === -1) return null;

  const sourceStepId = optionsFrom.slice(0, dotIndex);
  const sourceFieldKey = optionsFrom.slice(dotIndex + 1);
  const sourceItems = getByPath(formData, sourceStepId);

  if (!Array.isArray(sourceItems)) return new Set<string>();

  const keys = new Set<string>();
  sourceItems.forEach((item) => {
    const rawValue = getByPath(item, sourceFieldKey);
    if (rawValue === undefined || rawValue === null || rawValue === "") return;
    keys.add(String(rawValue));
  });

  return keys;
};

const sanitizeOutcomePolicyFormValue = (
  row: OutcomeRegistryRow,
  savedValue: unknown,
  formData?: Record<string, unknown>,
): OutcomePolicyFormValue => {
  const policyValue = getOutcomePolicyFormValue(row, savedValue);

  if (!formData) return policyValue;

  if (!row.componentsSelectable) {
    return {
      ...policyValue,
      selectedComponents: [],
    };
  }

  const selectableComponentKeys = getSelectableComponentKeys(row, formData);
  if (selectableComponentKeys === null) return policyValue;

  return {
    ...policyValue,
    selectedComponents: policyValue.selectedComponents.filter((component) =>
      selectableComponentKeys.has(component),
    ),
  };
};

export const buildOutcomeRegistryFormData = (
  outcomeRegistry: unknown,
  savedStepValue: unknown = {},
  formData?: Record<string, unknown>,
): Record<string, unknown> => {
  const stepValue =
    savedStepValue && typeof savedStepValue === "object"
      ? { ...(savedStepValue as Record<string, unknown>) }
      : {};
  const outcomes: Record<string, OutcomePolicyFormValue> =
    stepValue.outcomes && typeof stepValue.outcomes === "object"
      ? { ...(stepValue.outcomes as Record<string, OutcomePolicyFormValue>) }
      : {};

  const rows = getOutcomeRegistryRows(outcomeRegistry);

  rows.forEach((row) => {
    outcomes[row.key] = sanitizeOutcomePolicyFormValue(
      row,
      getSavedOutcomeValue(savedStepValue, row.key),
      formData,
    );
    delete stepValue[row.key];
  });

  return {
    ...stepValue,
    outcomes,
  };
};

export const applyOutcomeRegistryDefaults = (
  formData: Record<string, unknown>,
  steps: Array<{ stepId?: string; outcomeRegistry?: unknown }> = [],
): Record<string, unknown> => {
  const next: Record<string, unknown> = JSON.parse(
    JSON.stringify(formData || {}),
  );

  steps.forEach((step) => {
    if (!step?.stepId || !step?.outcomeRegistry) return;
    next[step.stepId] = buildOutcomeRegistryFormData(
      step.outcomeRegistry,
      next[step.stepId],
      next,
    );
  });

  return next;
};

export const getComponentAmount = (
  component: unknown,
  baseAmount: number,
): number => {
  const payoutComponent = component as PayoutComponent | undefined;
  if (
    !payoutComponent ||
    payoutComponent.value == null ||
    isNaN(Number(payoutComponent.value))
  )
    return 0;
  const val = Number(payoutComponent.value);
  if (payoutComponent.mode === PERCENT_OF_BASE_MODE) {
    return (val / 100) * (Number(baseAmount) || 0);
  }
  return val;
};

export const getOutcomeComponentAmount = (
  allComponents: PayoutComponent[] | undefined,
  componentType: string,
  baseAmount: number,
): number | null => {
  const component = (allComponents || []).find(
    (item) =>
      item &&
      item.componentType &&
      String(item.componentType).toLowerCase() === String(componentType).toLowerCase(),
  );
  return component ? getComponentAmount(component, baseAmount) : null;
};

export const getOutcomeTotal = (
  baseAmount: number,
  baseIncluded: boolean,
  selectedComponents: string[],
  allComponents: PayoutComponent[]
): number => {
  const base = baseIncluded ? (Number(baseAmount) || 0) : 0;
  const componentsAmount = selectedComponents.reduce(
    (sum, componentType) =>
      sum + (getOutcomeComponentAmount(allComponents, componentType, baseAmount) ?? 0),
    0,
  );
  return base + componentsAmount;
};

export const getOutcomeTotalLabel = (
  baseIncluded: boolean,
  selectedComponents: string[],
  getComponentLabel: (componentType: string) => string,
  baseLabel: string,
): string => {
  const parts: string[] = baseIncluded ? [baseLabel] : [];
  selectedComponents.forEach((componentType) => {
    parts.push(getComponentLabel(componentType));
  });
  return parts.join(" + ");
};